'use client';

import Link from 'next/link';
import React, { useCallback, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { IoArrowForward } from 'react-icons/io5';

import { PortalFlyout } from './header/PortalFlyout';
import { useFlyoutPanel } from './header/useFlyoutPanel';
import styles from './header.module.scss';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { selectIsLoggedIn } from '@/redux/auth/signin/slice';
import { fetchProductMenu } from '@/redux/products/thunk';
import type { MenuItem } from '@/redux/products/types';
import { useAuthGuard } from '@/hooks/useAuthGuard';

interface ServicesMenuProps {
  loginPath?: string;
}

const PANEL_ID = 'services-panel';
const TRIGGER_ID = 'services-trigger';

export default function ServicesMenu({ loginPath = '/account/signin' }: ServicesMenuProps) {
  const flyout = useFlyoutPanel({ id: PANEL_ID, width: 'wide' });

  const dispatch = useAppDispatch();
  const { data: menuItems, status: menuStatus } = useAppSelector(
    (s) => s.products.menu,
  );
  const isAuthenticated = useAppSelector(selectIsLoggedIn);

  useEffect(() => {
    if (menuStatus === 'idle') {
      dispatch(fetchProductMenu({}));
    }
  }, [dispatch, menuStatus]);

  return (
    <div className="relative inline-block">
      <button
        ref={flyout.triggerRef}
        id={TRIGGER_ID}
        type="button"
        aria-haspopup="menu"
        aria-expanded={flyout.isOpen}
        aria-controls={flyout.isOpen ? PANEL_ID : undefined}
        onClick={flyout.toggleByPointer}
        {...flyout.triggerProps}
        className={styles.trigger}
      >
        <span>Services</span>
        <FaChevronDown
          className={`text-[0.7rem] transition-transform duration-300 ${flyout.isOpen ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      <PortalFlyout
        flyout={flyout}
        id={PANEL_ID}
        ariaLabel="Services"
        ariaLabelledBy={TRIGGER_ID}
      >
        <ServicesMenuBody
          status={menuStatus}
          items={menuItems}
          isAuthenticated={isAuthenticated}
          loginPath={loginPath}
          closeMenu={flyout.close}
        />
      </PortalFlyout>
    </div>
  );
}

interface ServicesMenuBodyProps {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  items: MenuItem[];
  isAuthenticated: boolean;
  loginPath: string;
  closeMenu: () => void;
}

function ServicesMenuBody({
  status,
  items,
  isAuthenticated,
  loginPath,
  closeMenu,
}: ServicesMenuBodyProps) {
  if (status === 'loading') {
    return <p className="text-center w-full text-gray-400 text-sm py-6">Loading…</p>;
  }

  if (status === 'failed') {
    return (
      <p className="text-center w-full text-red-500 text-sm py-6">
        Failed to load services. Please try again later.
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-center w-full text-gray-500 text-sm py-6">
        No services are available at this time.
      </p>
    );
  }

  return (
    <div className="grid gap-x-8 gap-y-7 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
      {items.map((section) => (
        <ServicesSection
          key={section.id}
          section={section}
          isAuthenticated={isAuthenticated}
          loginPath={loginPath}
          closeMenu={closeMenu}
        />
      ))}
    </div>
  );
}

interface ServicesSectionProps {
  section: MenuItem;
  isAuthenticated: boolean;
  loginPath: string;
  closeMenu: () => void;
}

const ServicesSection = React.memo(function ServicesSection({
  section,
  isAuthenticated,
  loginPath,
  closeMenu,
}: ServicesSectionProps) {
  return (
    <section className="min-w-0 self-start">
      <h4
        className={styles.sectionTitle}
        title={section.description || section.name}
      >
        {section.name}
      </h4>
      <ul className="space-y-0.5">
        {section.subproducts.map((sub) => (
          <SubMenuItem
            key={sub.id}
            item={sub}
            rootId={section.id}
            parentId={section.id}
            isAuthenticated={isAuthenticated}
            loginPath={loginPath}
            closeMenu={closeMenu}
          />
        ))}
      </ul>
    </section>
  );
});

interface SubMenuItemProps {
  item: MenuItem;
  rootId: string | number;
  parentId: string | number;
  isAuthenticated: boolean;
  loginPath: string;
  closeMenu: () => void;
}

const SubMenuItem = React.memo(function SubMenuItem({
  item,
  rootId,
  parentId,
  isAuthenticated,
  loginPath,
  closeMenu,
}: SubMenuItemProps) {
  const { guard } = useAuthGuard({ loginPath, onRedirect: closeMenu });
  const href = `/services/application/${rootId}/i/${parentId}/${item.id}`;

  const onClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      if (guard(!!item.requires_auth, href)) {
        e.preventDefault();
        return;
      }
      closeMenu();
    },
    [guard, item.requires_auth, href, closeMenu],
  );

  return (
    <li>
      <Link
        href={href}
        role="menuitem"
        tabIndex={0}
        title={item.description || item.name}
        onClick={onClick}
        className={`${styles.menuitem} group/link flex items-center gap-1.5 whitespace-nowrap`}
      >
        <span className="min-w-0 truncate">{item.name}</span>
        <IoArrowForward
          aria-hidden
          className="shrink-0 translate-x-0 opacity-0 transition-all duration-150 group-hover/link:translate-x-0.5 group-hover/link:opacity-100 group-focus-visible/link:translate-x-0.5 group-focus-visible/link:opacity-100"
          aria-label={item.requires_auth && !isAuthenticated ? 'Login required' : undefined}
        />
      </Link>
    </li>
  );
});
